import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getFacilities,
  addFacility,
  editFacility,
  getFacility,
  submitFacility,
  rejectFacility,
  approveFacility,
  deleteFacility,
  getFacilityHistory,
  getLockedFacilities,
  startLoadingFacilities,
  stopLoadingFacilities,
  getFacilityAmounts,
  getFacilityAmount,
  getFacilityAmountRequest,
  getFacilityAmountFailure,
  facilityHoldChanged,
} from 'app/state/ducks/facilities/actions'
import { DraftFacilityParams, FacilityParams } from 'app/models/facility-params'
import {
  LiftFacilityHoldRequest,
  PlaceFacilityHoldRequest,
} from 'app/models/hold-params'
import { successFailureResponse } from 'app/utils/util-functions'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'
import { LOAD_FAILED, failed } from 'app/state/utils/load-result'

export const saveFacility =
  (savedFacility: DraftFacilityParams, facility?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const updated_savedFacility: DraftFacilityParams = {
      ...savedFacility,
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    }
    if (facility) {
      return client
        .put(`${apiServerUrl}/api/draft-facilities`, updated_savedFacility)
        .then(facility => {
          dispatch(editFacility(facility.data))
          return { payload: facility.data, success: true }
        })
        .catch(e => {
          return { payload: e.response.data.error, success: false }
        })
    } else {
      return client
        .post(`${apiServerUrl}/api/draft-facilities`, updated_savedFacility)
        .then(facility => {
          dispatch(addFacility(facility.data))
          return { payload: facility.data, success: true }
        })
        .catch(e => {
          return { payload: e.response.data.error, success: false }
        })
    }
  }

export const loadFacilities = coalesceThunkByKey(
  (deal: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/facilities/deal?id=${deal.id}`)
      .then(facilities => {
        return dispatch(getFacilities(facilities.data))
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  },
  (deal: any) => String(deal?.id ?? ''),
)

const loadApprovedFacilities = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/approved-facilities/`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

const loadDraftFacilities = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/draft-facilities`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

const loadSubmittedFacilities = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/submitted-facilities`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

export const loadAllFacilities = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingFacilities())
      const [draftFacilities, submittedFacilities, facilities] =
        await Promise.all([
          loadDraftFacilities(),
          loadSubmittedFacilities(),
          loadApprovedFacilities(),
        ])
      if ([draftFacilities, submittedFacilities, facilities].some(failed))
        return LOAD_FAILED

      const subFacilities = submittedFacilities.map((a: { facility: any }) => ({
        ...a.facility,
        status: 'Submitted',
      }))

      const allFacilities = draftFacilities
        .map((loan: any) => ({ ...loan, status: 'Draft' }))
        .concat(subFacilities)
        .concat(
          facilities.map((loan: any) => ({ ...loan, status: 'Approved' })),
        )

      return dispatch(getFacilities(allFacilities))
    } catch (error) {
      console.log('THUNK ERROR: ', error)
      return LOAD_FAILED
    } finally {
      dispatch(stopLoadingFacilities())
    }
  },
)

/** The facility's holds a place / lift answer carries (FacilityHoldResponse.holds), if any. */
const holdsIn = (answer: unknown): unknown[] | undefined =>
  (answer as { holds?: unknown[] } | undefined)?.holds

// Places a facility-level hold. The backend mints the Hold
// (lockId/placedByName/system/origin) — the frontend never fabricates it. Its
// answer carries the facility's holds as the transaction left them, and every
// store that shows them (the Facilities grid, the facility record, the
// facility list) takes them from that one dispatch: no re-read, which would
// go to PQS before it has projected the lock.
export const placeFacilityHold =
  (request: PlaceFacilityHoldRequest) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const { data: payload } = await client.post(
        `${apiServerUrl}/api/approved-facilities/hold`,
        request,
      )
      const holds = holdsIn(payload)
      dispatch(facilityHoldChanged({ facilityId: request.facilityId, holds }))
      // only an answer without the holds (an older backend) needs the lists re-read
      if (!holds) await loadAllFacilities()(dispatch as any)
      return { success: true, payload }
    } catch (err: any) {
      return {
        success: false,
        payload:
          err.response?.data?.error || 'Failed to place lock on facility',
      }
    }
  }

// Lifts the facility-level hold; the stores take the answer's holds as above.
export const liftFacilityHold =
  (request: LiftFacilityHoldRequest) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const { data: payload } = await client.delete(
        `${apiServerUrl}/api/approved-facilities/hold`,
        { data: request },
      )
      const holds = holdsIn(payload)
      dispatch(facilityHoldChanged({ facilityId: request.facilityId, holds }))
      // only an answer without the holds (an older backend) needs the lists re-read
      if (!holds) await loadAllFacilities()(dispatch as any)
      return { success: true, payload }
    } catch (err: any) {
      return {
        success: false,
        payload:
          err.response?.data?.error || 'Failed to lift lock on facility',
      }
    }
  }

/**
 * One facility's whole record, by id and state, returned to the caller and
 * NOT written to the single-facility store. For the row menus: a grid row is
 * a summary, and the edit, loan and termination forms want the full record —
 * but the page's own facility (getFacility) must not be replaced by a row
 * the user merely opened a menu on.
 */
export const fetchOneFacility =
  (facilityId: string, status: string) => async () => {
    const apiServerUrl = config.backendUrl
    const statusLower =
      status.toLowerCase() == 'terminated' ? 'locked' : status.toLowerCase()
    try {
      const response: any = await client.get(
        `${apiServerUrl}/api/${statusLower}-facilities/one?id=${facilityId}`,
      )
      const record =
        statusLower === 'submitted' || statusLower === 'locked'
          ? response.data.facility
          : response.data
      return { success: true, payload: { ...record, status } as FacilityParams }
    } catch (e: any) {
      return { success: false, payload: e?.response?.data?.error ?? e?.message }
    }
  }

export const setOneFacility = (facility: any) => async (dispatch: Dispatch) => {
  dispatch(getFacility({ ...facility }))
}

export const setAllFacilities =
  (allFacilities: any) => async (dispatch: Dispatch) => {
    dispatch(getFacilities(allFacilities))
  }

export const loadOneFacility = coalesceThunkByKey(
  (facilityId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower =
      status.toLowerCase() == 'terminated' ? 'locked' : status.toLowerCase()
    return client
      .get(
        `${apiServerUrl}/api/${statusLower}-facilities/one?id=${facilityId}`,
      )
      .then(facilities => {
        if (statusLower === 'submitted' || statusLower === 'locked') {
          return dispatch(
            getFacility({ ...facilities.data.facility, status: status }),
          )
        }
        return dispatch(getFacility({ ...facilities.data, status: status }))
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  },
)

export const submitForApproval =
  (facility: FacilityParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-facilities/submit`, { id: facility.id })
      .then(response => {
        dispatch(submitFacility(response.data))
        return { payload: response.data, success: true }
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  }

export const rejectSubmittedFacility =
  (facility: FacilityParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-facilities/reject`, {
        id: facility.id,
      })
      .then(response => {
        dispatch(rejectFacility(response.data))
        return { payload: response.data, success: true }
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  }

export const approveSubmittedFacility =
  (facility: FacilityParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-facilities/approve`, {
        id: facility.id,
      })
      .then(response => {
        dispatch(approveFacility(response.data))
        return { payload: response.data, success: true }
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  }

export const deleteDraftFacility =
  (ids: string[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/draft-facilities?ids=${ids}`)
      .then(response => {
        return dispatch(deleteFacility(response.data))
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
      })
  }

export const loadFacilityHistory = coalesceThunkByKey(
  (facilityId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-facilities/history?id=${facilityId}`)
      .then(response => {
        return dispatch(getFacilityHistory(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const loadAllFacilityAmounts = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-facilities/amounts`)
      .then(response => {
        return dispatch(getFacilityAmounts(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const readOneFacilityAmounts = (facilityId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/approved-facilities/amounts/one?id=${facilityId}`)
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.log('THUNK ERROR: ', error)
    })
}

export const loadOneFacilityAmounts = coalesceThunkByKey(
  (facilityId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    dispatch(getFacilityAmountRequest())
    return client
      .get(
        `${apiServerUrl}/api/approved-facilities/amounts/one?id=${facilityId}`,
      )
      .then(response => {
        return dispatch(getFacilityAmount(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
        return dispatch(getFacilityAmountFailure())
      })
  },
)

export const readFacilityHistory = (facilityId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/approved-facilities/history?id=${facilityId}`)
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.log('THUNK ERROR: ', error)
    })
}

export const loadLockedFacilities = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/locked-facilities`)
      .then(response => {
        const lockedFacility = response.data.map(
          (a: { facility: any; locked: string }) => ({
            ...a.facility,
            status: 'Terminated',
            terminationDate: a.locked,
          }),
        )
        return dispatch(getLockedFacilities(lockedFacility))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const loadFacilitySnapshot = (facilityId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/approved-facilities/snapshot?id=${facilityId}`)
    .then(facility => {
      return { success: true, payload: facility.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const createOrEditBulkFacilities =
  (facilities: FacilityParams[]) => async () => {
    const apiServerUrl = config.backendUrl
    console.log('bulk facilities', facilities)
    return client
      .post(`${apiServerUrl}/api/draft-facilities/bulk`, {
        facilityList: facilities,
      })
      .then(response => {
        console.log('reponse', response)
        const goodResp = successFailureResponse(response.data)
        console.log('goodResp', goodResp)
        return { success: true, payload: response.data }
      })
      .catch(e => {
        console.log('facilities bulk upload error', e)
        return { success: false, payload: e.response.data.error }
      })
  }

export const submitBulkFacilities =
  (facilities: FacilityParams[]) => async () => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-facilities/bulk_submit`, {
        facilityList: facilities,
      })
      .then(response => {
        console.log('response bulk', response)
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveBulkFacilities =
  (facilities: FacilityParams[]) => async () => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-facilities/bulk_approve`, {
        facilityList: facilities,
      })
      .then(response => {
        console.log('response bulk', response)
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }
